import { NestSlidesDocument, UnnestSlidesDocument } from "@/generated/graphql";
import { withToast } from "@/lib/utils/withToast";
import type { ApolloCache } from "@apollo/client";
import { useMutation } from "@apollo/client/react";

/**
 * Nesting moves a slide between two lists the cache holds separately — the lesson's slides and a
 * main slide's `children` — so drop both and let the lesson query refill them.
 */
function evictSlideLists(cache: ApolloCache) {
  cache.evict({ id: "ROOT_QUERY", fieldName: "slidesByLessonId" });
  cache.gc();
}

export function useNestSlides() {
  const [nestSlidesMutation, { loading: nestLoading }] = useMutation(NestSlidesDocument, {
    update: evictSlideLists,
  });
  const [unnestSlidesMutation, { loading: unnestLoading }] = useMutation(UnnestSlidesDocument, {
    update: evictSlideLists,
  });

  const nestSlides = (parentSlideId: string, slideIds: string[]) =>
    withToast(
      async () => {
        const result = await nestSlidesMutation({ variables: { parentSlideId, slideIds } });
        return result.data?.nestSlides ?? [];
      },
      {
        loading: "Making subslide...",
        success: "Slide is now a subslide",
        error: "Failed to make this slide a subslide. Please try again.",
      },
      "nestSlides"
    );

  const unnestSlides = (slideIds: string[]) =>
    withToast(
      async () => {
        const result = await unnestSlidesMutation({ variables: { slideIds } });
        return result.data?.unnestSlides ?? [];
      },
      {
        loading: "Promoting...",
        success: "Subslide is now its own slide",
        error: "Failed to promote this subslide. Please try again.",
      },
      "unnestSlides"
    );

  return { nestSlides, unnestSlides, loading: nestLoading || unnestLoading };
}
